/* eslint-disable react/prop-types */
import { type CSSProperties, forwardRef, type ReactNode } from 'react';

import { css } from '@compiled/react';

import { token } from '@atlaskit/tokens';

export type CursorColor = 'yellow' | 'blue';
export type CursorVariant = 'live' | 'demo';

export type JsmPlaygroundCursorProps = {
	label?: ReactNode;
	color?: CursorColor;
	/** Live follows the pointer. Demo is the scripted arrow used inside the previews. */
	variant?: CursorVariant;
	isActive?: boolean;
	isClicking?: boolean;
	isHovering?: boolean;
	style?: CSSProperties;
	className?: string;
	dataCustomCursor?: string;
	testId?: string;
	dataVisible?: boolean;
};

const getCursorId = (dataCustomCursor?: string, label?: ReactNode): string => {
	if (dataCustomCursor) {
		return dataCustomCursor;
	}
	return typeof label === 'string' ? label.toLowerCase() : 'custom';
};

const CursorPointer = ({
	isDemo,
	isBlue,
	isClicking,
	isHovering,
}: {
	isDemo: boolean;
	isBlue: boolean;
	isClicking: boolean;
	isHovering: boolean;
}) => {
	if (isDemo) {
		return (
			<svg
				css={[demoPointerStyles, isClicking && demoPointerPressedStyles]}
				fill="none"
				height="24"
				viewBox="0 0 23 23"
				width="24"
				aria-hidden="true"
			>
				<path
					d="M7.735 22.62 0 0l22.585 7.771-9.899 4.951-4.951 9.898Z"
					fill="currentColor"
					stroke="white"
					strokeWidth="1.5"
				/>
			</svg>
		);
	}

	return (
		<svg
			css={[
				customCursorPointerStyles,
				isBlue && pointerBlueStyles,
				isHovering && pointerHoveringStyles,
				isClicking && pointerClickingStyles,
			]}
			width="24"
			height="48"
			viewBox="0 0 23 23"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
			aria-hidden="true"
		>
			<path
				d="M7.73535 22.6201L0.000042 0.000059L22.5846 7.77092L12.6866 12.7218L7.73535 22.6201Z"
				fill="currentColor"
			/>
		</svg>
	);
};

const CursorLabel = ({
	isDemo,
	isBlue,
	isClicking,
	isHovering,
	label,
}: {
	isDemo: boolean;
	isBlue: boolean;
	isClicking: boolean;
	isHovering: boolean;
	label: ReactNode;
}) => (
	<div
		css={[
			cursorLabelStyles,
			isDemo ? demoLabelStyles : liveLabelStyles,
			!isDemo && isBlue && labelBlueStyles,
			!isDemo && isHovering && labelHoveringStyles,
			!isDemo && isClicking && labelClickingStyles,
		]}
	>
		{label}
	</div>
);

export const JsmPlaygroundCursor = forwardRef<HTMLDivElement, JsmPlaygroundCursorProps>(
	(props, ref) => {
		const {
			label = 'You',
			color = 'yellow',
			variant = 'live',
			isActive = false,
			isClicking = false,
			isHovering = false,
			style,
			dataCustomCursor,
			testId,
			dataVisible,
		} = props;

		const isDemo = variant === 'demo';
		const isBlue = color === 'blue';
		const cursorId = getCursorId(dataCustomCursor, label);

		return (
			<div
				ref={ref}
				aria-hidden="true"
				data-custom-cursor={cursorId}
				data-cursor-color={color}
				data-cursor-active={String(isActive)}
				data-cursor-hovering={String(isHovering)}
				data-cursor-clicking={String(isClicking)}
				data-testid={testId}
				data-visible={dataVisible === undefined ? undefined : String(dataVisible)}
				style={style}
				css={[
					isDemo ? demoCursorStyles : customCursorStyles,
					!isDemo && isActive && customCursorVisibleStyles,
				]}
			>
				<div css={cursorBodyStyles}>
					<CursorPointer
						isBlue={isBlue}
						isClicking={isClicking}
						isDemo={isDemo}
						isHovering={isHovering}
					/>
					<CursorLabel
						isBlue={isBlue}
						isClicking={isClicking}
						isDemo={isDemo}
						isHovering={isHovering}
						label={label}
					/>
				</div>
			</div>
		);
	},
);

JsmPlaygroundCursor.displayName = 'JsmPlaygroundCursor';

export default JsmPlaygroundCursor;

const customCursorStyles = css({
	position: 'absolute',
	insetBlockStart: 0,
	insetInlineStart: 0,
	zIndex: 5,
	width: 0,
	height: 0,
	opacity: 0,
	pointerEvents: 'none',
	willChange: 'transform',
	transform: 'translate3d(var(--cursor-x, 0px), var(--cursor-y, 0px), 0)',
	transition: 'opacity 120ms ease',
	'@media (pointer: coarse)': {
		display: 'none',
	},
});

const demoCursorStyles = css({
	position: 'absolute',
	insetBlockStart: 0,
	insetInlineStart: 0,
	zIndex: 700,
	pointerEvents: 'none',
	opacity: 'var(--demo-cursor-opacity)',
	transform:
		'translate3d(var(--demo-cursor-x), var(--demo-cursor-y), 0) scale(var(--demo-cursor-scale))',
	transformOrigin: 'top left',
	transition:
		'var(--demo-cursor-transition), opacity var(--demo-cursor-fade, 400ms) cubic-bezier(0.33, 0, 0.2, 1)',
	'@media (prefers-reduced-motion: reduce)': {
		transition: 'none',
	},
});

const customCursorVisibleStyles = css({
	opacity: 1,
});

const cursorBodyStyles = css({
	position: 'relative',
	width: 0,
	height: 0,
});

const cursorLabelStyles = css({
	position: 'absolute',
	insetBlockStart: token('space.150'),
	insetInlineStart: token('space.300'),
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'center',
	paddingInline: token('space.150'),
	borderRadius: token('radius.full'),
	font: token('font.heading.medium'),
	fontWeight: token('font.weight.semibold'),
	whiteSpace: 'nowrap',
	boxShadow: token('elevation.shadow.overlay'),
});

const liveLabelStyles = css({
	paddingBlock: token('space.050'),
	backgroundColor: token('color.background.warning.bold'),
	color: token('color.text'),
	transition:
		'transform 250ms cubic-bezier(0.22, 1, 0.36, 1), background-color 150ms ease, color 150ms ease',
});

const demoLabelStyles = css({
	paddingBlock: token('space.050'),
	backgroundColor: token('color.background.accent.blue.bolder'),
	color: token('color.text.inverse'),
});

const labelBlueStyles = css({
	backgroundColor: token('color.background.brand.bold'),
	color: token('color.text.inverse'),
});

const labelHoveringStyles = css({
	transform: 'translate(4px, 8px)',
});

const labelClickingStyles = css({
	transform: 'translate(-2px, -3px)',
});

const customCursorPointerStyles = css({
	position: 'absolute',
	insetBlockStart: '-16px',
	insetInlineStart: 0,
	width: '24px',
	height: '48px',
	transform: 'scale(1)',
	transformOrigin: '0 0',
	color: token('color.background.warning.bold'),
	transition:
		'transform 250ms cubic-bezier(0.22, 1, 0.36, 1), filter 250ms cubic-bezier(0.22, 1, 0.36, 1), color 150ms ease',
});

const pointerBlueStyles = css({
	color: token('color.background.brand.bold'),
});

const pointerHoveringStyles = css({
	transform: 'scale(1.3)',
	// eslint-disable-next-line local/ensure-wac-token-usage
	filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.25))',
});

const pointerClickingStyles = css({
	transform: 'scale(0.92)',
	filter: 'none',
});

const demoPointerStyles = css({
	position: 'absolute',
	insetBlockStart: 0,
	insetInlineStart: 0,
	color: token('color.background.accent.blue.bolder'),
	filter: `drop-shadow(0 2px 6px ${token('color.blanket')})`,
	transform: 'scale(1)',
	transformOrigin: 'top left',
	transition: 'transform 200ms cubic-bezier(0.33, 0, 0.2, 1)',
	'@media (prefers-reduced-motion: reduce)': {
		transition: 'none',
	},
});

const demoPointerPressedStyles = css({
	transform: 'scale(0.9)',
});
